import React, { useMemo } from 'react';

const CampusHeatmap = ({ data, campusId = "C0000000", onClose }) => {
  const heatmapData = useMemo(() => {
    const sortedData = [...data].sort((a, b) => new Date(a.roachDate) - new Date(b.roachDate));
    const dates = [...new Set(sortedData.map(r => r.roachDate))].sort();

    const dimensions = [
      { id: 0, label: "🌪 Chaos", short: "Chaos" },
      { id: 1, label: "🔥 Heat", short: "Heat" },
      { id: 2, label: "🎭 Humor", short: "Humor" },
      { id: 3, label: "🧊 Numb", short: "Numb" },
      { id: 4, label: "⚡ Focus", short: "Focus" },
      { id: 5, label: "📱 Doom", short: "Doom" },
      { id: 6, label: "🧠 Recovery", short: "Recovery" },
    ];

    // Transposed: Dates as rows, Dimensions as columns
    const rows = dates.map(date => {
      const dayEntries = sortedData.filter(r => r.roachDate === date);
      const cells = dimensions.map(dim => {
        let total = 0, count = 0;
        dayEntries.forEach(entry => {
          const levers = entry.levers ? entry.levers.split(',').map(Number) : [];
          if (levers[dim.id] !== undefined) {
            total += levers[dim.id];
            count++;
          }
        });
        const value = count > 0 ? parseFloat((total / count).toFixed(1)) : null;
        return { dim, value };
      });

      return { date, cells };
    });

    return { dates, dimensions, rows };
  }, [data]);

  const getHeatColor = (value) => {
    if (value === null) return 'bg-gray-900 text-gray-500 border border-gray-700';
    if (value >= 8) return 'bg-emerald-500 text-white';
    if (value >= 6.5) return 'bg-emerald-600 text-white';
    if (value >= 5) return 'bg-yellow-500 text-black';
    if (value >= 3.5) return 'bg-orange-500 text-white';
    return 'bg-rose-600 text-white';
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-3">
      <div className="bg-[#111827] border border-gray-700 rounded-3xl w-full max-w-[98vw] max-h-[95vh] overflow-hidden flex flex-col">
        
        {/* Compact Header */}
        <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-100 tracking-tight">Campus Heatmap</h2>
            <p className="text-xs text-gray-400">7 Dimensions • {campusId}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-800"
          >
            ✕
          </button>
        </div>

        {/* Heatmap - Transposed (No horizontal scroll) */}
        <div className="flex-1 overflow-auto p-3">
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-[#111827] z-20 p-2 text-left text-xs font-medium text-gray-400 border-b border-gray-700 w-20 lg:w-24">
                    Date
                  </th>
                  {heatmapData.dimensions.map((dim, i) => (
                    <th
                      key={i}
                      className="bg-[#111827] z-10 p-2 text-center text-[10px] font-medium text-gray-400 border-b border-gray-700 whitespace-nowrap min-w-[52px]"
                    >
                      {dim.short}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmapData.rows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="border-b border-gray-800 hover:bg-gray-900/40">
                    <td className="sticky left-0 bg-[#111827] p-2 text-xs font-medium whitespace-nowrap border-r border-gray-700 text-gray-300">
                      {new Date(row.date).toLocaleDateString('en-US', { 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    {row.cells.map((cell, cellIdx) => (
                      <td key={cellIdx} className="p-1 text-center">
                        <div
                          className={`w-8 h-8 lg:w-9 lg:h-9 mx-auto rounded-xl flex items-center justify-center text-[10px] lg:text-xs font-bold transition-all active:scale-95 ${getHeatColor(cell.value)}`}
                        >
                          {cell.value !== null ? cell.value : '—'}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Compact Legend */}
        <div className="border-t border-gray-700 px-4 py-2.5 flex flex-wrap items-center gap-3 text-[10px] text-gray-400">
          <div className="flex items-center gap-1">
            <div className="w-3.5 h-3.5 bg-emerald-500 rounded"></div> Strong
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3.5 h-3.5 bg-yellow-500 rounded"></div> Mod
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3.5 h-3.5 bg-rose-600 rounded"></div> Critical
          </div>
          <div className="ml-auto text-[9px] text-gray-500">↑ Better survival</div>
        </div>
      </div>
    </div>
  );
};

export default CampusHeatmap;